import { useEffect, useState } from "react";

import { Attribute, QuickRankMode } from "../../gql/graphql";
import { calculateOffsetLeft, calculateOffsetTop } from "../../utils/offset";
import { QUICKRANK_RATING_DESTINATION_ID, QUICKRANK_ROOT_ID } from "../../constants";
import { QuickRankRenderableDestinations } from "../../reducers/quickrank";
import { useQuickRankDestinations } from "./useQuickRankDestinations";
import { useQuickRankState } from "./useQuickRankState";

export type Position = {
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
};

const { RATED, HAND } = QuickRankRenderableDestinations;

export type CardPath = {
  [key in QuickRankRenderableDestinations]?: HTMLDivElement;
};

export type CardPositions = {
  [key in QuickRankRenderableDestinations]?: Position;
};

export const useQuickRankCardPosition = (card: {
  index: number;
  attribute: Attribute;
}): {
  [key in QuickRankRenderableDestinations]?: Position;
} => {
  const { refs: destinations, resizeClock } = useQuickRankDestinations();
  const [
    {
      cards,
      configuration: { mode },
    },
  ] = useQuickRankState();
  const [cardPath, setCardPath] = useState<CardPath>({});
  const [cardPositions, setCardPositions] = useState<CardPositions>({});

  const updateCardPath = (destination: QuickRankRenderableDestinations) => {
    const shouldUpdateHandPath = mode === QuickRankMode.Optimized && destination === HAND;
    if (cardPath[destination] && !shouldUpdateHandPath) {
      return;
    }
    const destinationCards = cards[destination];
    const index = destinationCards?.findIndex((c) => c?.index === card.index);
    if (index >= 0) {
      const ref = destinations[destination].ref;
      const element = ref.current;
      if (!element) {
        return;
      }
      const destinationEl = element.querySelector(
        // In case of rated, every card has the same destination
        destination === RATED ? `#${QUICKRANK_RATING_DESTINATION_ID}` : `#destination-${destination}-${index}`,
      );
      if (!destinationEl) {
        return;
      }

      setCardPath((prev) => ({
        ...prev,
        [destination]: destinationEl,
      }));
    } else {
      setCardPath((prev) => ({
        ...prev,
        [destination]: undefined,
      }));
    }
  };

  const countPosition = (destination: QuickRankRenderableDestinations) => {
    const pathElement = cardPath[destination];
    if (!pathElement && destination !== HAND) {
      return;
    } else if (!pathElement) {
      return setCardPositions((prev) => ({
        ...prev,
        [destination]: undefined,
      }));
    }
    const { width, height } = pathElement.getBoundingClientRect();

    const top = calculateOffsetTop(pathElement, QUICKRANK_ROOT_ID);
    const left = calculateOffsetLeft(pathElement, QUICKRANK_ROOT_ID);

    setCardPositions((prev) => ({
      ...prev,
      [destination]: {
        x: left,
        y: top,
        width,
        height,
        index: pathElement.id?.split("-")[2] || 0,
      },
    }));
  };

  // Map through every element of QuickRankRenderableDestinations
  // and update cardPath and cardPositions
  for (const destination of Object.values(QuickRankRenderableDestinations)) {
    useEffect(() => updateCardPath(destination), [cards[destination]]);
    useEffect(() => countPosition(destination), [cardPath[destination], resizeClock]);
  }

  return cardPositions;
};
